﻿import { AttributeValues, CodeEditFields } from '@/shared/codeedit/codeEditFields.ts';
import { CaseSensitiveCodeRefs, ReferenceFilterNode, ReferenceFilterOperator, ReferenceFilterValue } from '@/shared/codeedit/dynamicFilterTypes.ts';
import { normalizeTemporal, TypeGroup } from '@/shared/codeedit/dynamicFilterRules.ts';
import { CodeEditContextRefCode } from '@/shared/helpers/api.ts';

/*
Pure client-side evaluator for dynamic CodeRef filter predicates (ReferenceFilterDynamic).
Given a predicate, the option's DynamicFilterReferenceValues and the current code's field values, decides
whether the option should remain selectable. No SQL and no network calls, the predicate is
evaluated entirely against data already loaded into the browser.
 */

// The outcome of a predicate for one option: whether it matched and, when it did not, the code
// fields whose current values caused the mismatch, and the nodes that failed, each group before
// the nodes it holds
export type ReferenceFilterResult = {
    matched: boolean;
    failing: string[];
    failingNodes: ReferenceFilterNode[];
};

// Shared because a match has nothing to report. Callers must treat the result as read-only.
const MATCHES: ReferenceFilterResult = { matched: true, failing: [], failingNodes: [] };

// Missing predicate means no filtering: every option matches. caseSensitiveCodeRefs are the CodeRef
// fields that point into case-sensitive libraries; left out, none do.
export function evaluateReferenceFilter(
    predicate: ReferenceFilterNode | null,
    optionFilterValues: AttributeValues | null,
    codeFieldValues: CodeEditFields,
    caseSensitiveCodeRefs?: CaseSensitiveCodeRefs
): ReferenceFilterResult {
    if (!predicate) {
        return MATCHES;
    }
    const { result, failingNodes } = evaluateNode(predicate, optionFilterValues, codeFieldValues, caseSensitiveCodeRefs);
    if (result !== false) {
        return MATCHES;
    }
    const failing = failingNodes.map(node => node.param).filter((param): param is string => !!param);
    return { matched: false, failing: [...new Set(failing)], failingNodes: [...failingNodes] };
}

type NodeResult = boolean | 'skip';

// Shared because a matching node has nothing to report
const NO_FAILING: readonly ReferenceFilterNode[] = [];

type DetailedNodeResult = { result: NodeResult; failingNodes: readonly ReferenceFilterNode[] };

function evaluateNode(
    node: ReferenceFilterNode,
    optionFilterValues: AttributeValues | null,
    codeFieldValues: CodeEditFields,
    caseSensitiveCodeRefs?: CaseSensitiveCodeRefs
): DetailedNodeResult {
    if (node.op === 'And' || node.op === 'Or') {
        const decided = (node.clauses ?? [])
            .map(clause => evaluateNode(clause, optionFilterValues, codeFieldValues, caseSensitiveCodeRefs))
            .filter(clause => clause.result !== 'skip');
        if (decided.length === 0) {
            return { result: 'skip', failingNodes: NO_FAILING };
        }
        const result = node.op === 'And'
            ? decided.every(clause => clause.result === true)
            : decided.some(clause => clause.result === true);
        if (result) {
            return { result, failingNodes: NO_FAILING };
        }
        // A failing group comes before what its clauses report, so its own param explains the failure first
        const failing = decided.filter(clause => clause.result === false).flatMap(clause => clause.failingNodes);
        return { result, failingNodes: [node, ...failing] };
    }

    const result = evaluateComparison(node, optionFilterValues, codeFieldValues, caseSensitiveCodeRefs);
    if (result !== false) {
        return { result, failingNodes: NO_FAILING };
    }
    return { result, failingNodes: [node] };
}

// Whether a rule compares exactly: a text rule when it is set to, and a CodeRef rule when the fields
// it compares point into case-sensitive libraries. Nothing else has case.
// Keep in sync with IsCaseSensitive in ReferenceFilterEvaluator.cs.
export function isCaseSensitive(node: ReferenceFilterNode, caseSensitiveCodeRefs?: CaseSensitiveCodeRefs): boolean {
    switch (node.group) {
        case 'Text':
            return node.caseSensitive === true;
        case 'CodeRef':
            return !!caseSensitiveCodeRefs
                && !!node.attribute
                && includesIgnoringCase(caseSensitiveCodeRefs.attributes, node.attribute)
                && (!node.param || includesIgnoringCase(caseSensitiveCodeRefs.params, node.param));
        default:
            return false;
    }
}

// Field names match ignoring case, as on the server
function includesIgnoringCase(names: string[], name: string): boolean {
    const lowerName = name.toLowerCase();
    return names.some(candidate => candidate.toLowerCase() === lowerName);
}

function evaluateComparison(
    node: ReferenceFilterNode,
    optionFilterValues: AttributeValues | null,
    codeFieldValues: CodeEditFields,
    caseSensitiveCodeRefs?: CaseSensitiveCodeRefs
): NodeResult {
    const optionValue: ReferenceFilterValue = (node.attribute ? optionFilterValues?.[node.attribute] : null) ?? null;
    const group = node.group ?? 'Unknown';
    const exact = isCaseSensitive(node, caseSensitiveCodeRefs);

    // In compares against a fixed list and is never bound to a form field, so it has no value to resolve
    if (node.op === 'In') {
        return matchesIn(optionValue, node.values ?? [], group, exact);
    }

    const bound = resolveBoundValue(node, codeFieldValues);
    // Depends on a code field that has no value yet
    if (bound.skip) {
        return 'skip';
    }

    switch (node.op) {
        case 'Eq':
            return valuesEqual(optionValue, bound.value, group, exact);
        case 'Neq':
            return !valuesEqual(optionValue, bound.value, group, exact);
        case 'Gt':
            return orderMatches(optionValue, bound.value, group, exact, c => c > 0);
        case 'Lt':
            return orderMatches(optionValue, bound.value, group, exact, c => c < 0);
        case 'Ge':
            return orderMatches(optionValue, bound.value, group, exact, c => c >= 0);
        case 'Le':
            return orderMatches(optionValue, bound.value, group, exact, c => c <= 0);
        case 'Exact':
        case 'Prefix':
        case 'Suffix':
        case 'Contains':
            return matchesLike(optionValue, bound.value, node.op, exact);
        default:
            return 'skip';
    }
}

// True when no clause can rule anything out, because every one of them depends on a code field that
// is still empty and so evaluates to 'skip'.
export function predicateMatchesEverything(predicate: ReferenceFilterNode, codeFieldValues: CodeEditFields): boolean {
    if (predicate.op === 'And' || predicate.op === 'Or') {
        return (predicate.clauses ?? []).every(clause => predicateMatchesEverything(clause, codeFieldValues));
    }
    return predicate.op !== 'In' && !!predicate.param && isEmpty(codeFieldValues[predicate.param]);
}

export function referenceFilterMismatchMessage(failing: string[]): string {
    return failing.length > 0
        ? `Does not match current value of: ${failing.join(', ')}`
        : 'Filtered by dynamic filter';
}

// Every distinct value the selector yields across a predicate tree (e.g. all `param` or `attribute` names).
function collectPredicateValues(node: ReferenceFilterNode, selector: (n: ReferenceFilterNode) => string | null | undefined): string[] {
    if (node.op === 'And' || node.op === 'Or') {
        const values = (node.clauses ?? []).flatMap(clause => collectPredicateValues(clause, selector));
        return [...new Set(values)];
    }
    const value = selector(node);
    return value ? [value] : [];
}

// Detects a predicate that depends on an attribute or param that no longer exists because it was deleted
// after the filter was saved:
// - `param` binds to a field on the same code, so it must be one of the code's own current field names.
// - `attribute` is a column in the reference library. If it has been deleted there, or its type is not
// filterable, no loaded option will have that key in dynamicFilterReferenceValues (CodeEditContextFactory drops
// unknown filter columns, and leaves the whole property out when none are left, rather than letting
// the whole load fail, see its GetKnownReferenceColumns).
// Returns the name of the first broken dependency found, or null if the predicate's dependencies all still exist.
export function brokenDynamicFilterDependency(
    predicate: ReferenceFilterNode,
    ownFieldNames: ReadonlySet<string>,
    referenceOptions?: CodeEditContextRefCode[]
): string | null {
    const params = collectPredicateValues(predicate, n => n.param);
    const missingParam = params.find(p => !ownFieldNames.has(p));
    if (missingParam) {
        return missingParam;
    }

    if (referenceOptions && referenceOptions.length > 0) {
        const attributes = collectPredicateValues(predicate, n => n.attribute);
        const missingAttribute = attributes.find(
            attribute => !referenceOptions.some(option => option.dynamicFilterReferenceValues !== undefined
                && Object.hasOwn(option.dynamicFilterReferenceValues, attribute))
        );
        if (missingAttribute) {
            return missingAttribute;
        }
    }

    return null;
}

type BoundValue = { skip: true } | { skip: false; value: ReferenceFilterValue };

// Resolves the right-hand side of a comparison: a literal (node.value) or a code field (node.param)
function resolveBoundValue(node: ReferenceFilterNode, codeFieldValues: CodeEditFields): BoundValue {
    if (node.param) {
        const fieldValue = codeFieldValues[node.param];
        if (isEmpty(fieldValue)) {
            return { skip: true };
        }
        return { skip: false, value: fieldValue };
    }
    return { skip: false, value: node.value ?? null };
}

// Text of only spaces is no value either. Keep in sync with IsEmpty in ReferenceFilterEvaluator.cs.
// TypeScript has no type for such text, so the guard names '' for it.
function isEmpty(value?: ReferenceFilterValue): value is null | undefined | '' {
    return value === null || value === undefined || (typeof value === 'string' && value.trim() === '');
}

// Whether a value changed as a rule sees it: a time below a second is no change, nor is casing
// unless the rule is case-sensitive. Keep in sync with Differs in ReferenceFilterEvaluator.cs.
export function valueChangedForFilter(group: TypeGroup, before?: ReferenceFilterValue, after?: ReferenceFilterValue, caseSensitive = false): boolean {
    if (isEmpty(before) || isEmpty(after)) {
        return isEmpty(before) !== isEmpty(after);
    }
    return compareValues(before, after, group, caseSensitive) !== 0;
}

export function compareValues(a: ReferenceFilterValue, b: ReferenceFilterValue, group: TypeGroup, exact = false): number | null {
    switch (group) {
        case 'Numeric': {
            const left = Number(a);
            const right = Number(b);
            if (!Number.isFinite(left) || !Number.isFinite(right)) return null;
            return left - right;
        }
        case 'Bool': {
            const left = toBool(a);
            const right = toBool(b);
            if (left === null || right === null) return null;
            return Number(left) - Number(right);
        }
        case 'Temporal': {
            const left = normalizeTemporal(a);
            const right = normalizeTemporal(b);
            if (left === null || right === null) return null;
            return new Date(left).getTime() - new Date(right).getTime();
        }
        default:
            return exact ? compareOrdinal(String(a), String(b)) : compareOrdinal(String(a).toLowerCase(), String(b).toLowerCase());
    }
}

// Orders by UTF-16 code unit, the same as string.CompareOrdinal on the server, so MW and mW differ,
function compareOrdinal(a: string, b: string): number {
    if (a === b) return 0;
    return a < b ? -1 : 1;
}

function toBool(value: ReferenceFilterValue): boolean | null {
    if (typeof value === 'boolean') return value;
    const text = String(value).trim().toLowerCase();
    if (text === 'true' || text === '1') return true;
    if (text === 'false' || text === '0') return false;
    return null;
}

// Two missing values are equal, one missing value never matches a present one
function valuesEqual(a: ReferenceFilterValue, b: ReferenceFilterValue, group: TypeGroup, exact: boolean): boolean {
    if (isEmpty(a) || isEmpty(b)) {
        return isEmpty(a) && isEmpty(b);
    }
    return compareValues(a, b, group, exact) === 0;
}

function orderMatches(
    a: ReferenceFilterValue,
    b: ReferenceFilterValue,
    group: TypeGroup,
    exact: boolean,
    test: (comparison: number) => boolean
): boolean {
    if (isEmpty(a) || isEmpty(b)) {
        return false;
    }
    const comparison = compareValues(a, b, group, exact);
    return comparison !== null && test(comparison);
}

function matchesLike(optionValue: ReferenceFilterValue, pattern: ReferenceFilterValue, op: ReferenceFilterOperator, exact: boolean): boolean {
    if (isEmpty(optionValue) || isEmpty(pattern)) {
        return false;
    }
    const value = exact ? String(optionValue) : String(optionValue).toLowerCase();
    const patternValue = exact ? String(pattern) : String(pattern).toLowerCase();

    switch (op) {
        case 'Prefix':
            return value.startsWith(patternValue);
        case 'Suffix':
            return value.endsWith(patternValue);
        case 'Contains':
            return value.includes(patternValue);
        case 'Exact':
        default:
            return value === patternValue;
    }
}

function matchesIn(optionValue: ReferenceFilterValue, list: string[], group: TypeGroup, exact: boolean): boolean {
    if (isEmpty(optionValue)) {
        return false;
    }
    return list.some(item => valuesEqual(optionValue, item, group, exact));
}
